import { useEffect, useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Typography, Space } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined, MedicineBoxOutlined, BankOutlined, TeamOutlined,
  UserOutlined, CalendarOutlined, FileTextOutlined, HeartOutlined,
  LogoutOutlined, SettingOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../store/auth.store';
import { useAuth } from '../hooks/useAuth';
import { usePermission } from '../hooks/usePermission';
import { USER_ROLES } from '../utils/constants';
import ProfileModal from '../components/common/ProfileModal';
import { resolveFileUrl } from '../utils/helpers';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const menuItems = [
  { key: '/dashboard',     icon: <DashboardOutlined />,   label: 'Dashboard',     roles: 'all' },
  { key: '/clinics',       icon: <MedicineBoxOutlined />, label: 'Clinics',       roles: [USER_ROLES.ADMIN] },
  { key: '/schools',       icon: <BankOutlined />,        label: 'Schools',       roles: [USER_ROLES.ADMIN] },
  { key: '/staff',         icon: <TeamOutlined />,        label: 'Staff',         roles: [USER_ROLES.ADMIN] },
  { key: '/doctors',       icon: <UserOutlined />,        label: 'Doctors',       roles: [USER_ROLES.ADMIN] },
  { key: '/children',      icon: <HeartOutlined />,    label: 'Children',      roles: [USER_ROLES.ADMIN, USER_ROLES.SCHOOL_STAFF] },
  { key: '/events',        icon: <CalendarOutlined />,  label: 'Events',        roles: 'all' },
  { key: '/referrals',     icon: <SolutionOutlined />,  label: 'Referred Children', roles: [USER_ROLES.ADMIN, USER_ROLES.DOCTOR] },
  // { key: '/registrations', icon: <FileTextOutlined />,  label: 'Registrations', roles: [USER_ROLES.ADMIN, USER_ROLES.SCHOOL_STAFF] },
];

const AppLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const { logout } = useAuth();
  const { role, isReferralDoctor } = usePermission();

  const filteredMenuItems = menuItems.filter((item) => {
    if (isReferralDoctor) { return item.key === '/referrals' || item.key === '/dashboard'; }
    if (item.roles === 'all') { return true; }
    return item.roles.includes(role);
  });

  const userMenuItems = [
    { key: 'profile', icon: <SettingOutlined />, label: 'Profile' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true },
  ];

  const handleUserMenuClick = ({ key }) => {
    if (key === 'logout') { logout(); }
    if (key === 'profile') { setProfileOpen(true); }
  };

  const roleLabel = {
    admin: 'Administrator',
    school_staff: 'School Staff',
    doctor: 'Doctor',
  };

  const orgInfo = (() => {
    if (user?.role === USER_ROLES.ADMIN) { return { icon: <BankOutlined />, label: 'WeCircle' }; }
    if (user?.role === USER_ROLES.DOCTOR && user?.clinicName) { return { icon: <MedicineBoxOutlined />, label: user.clinicName }; }
    if (user?.role === USER_ROLES.SCHOOL_STAFF && user?.schoolName) { return { icon: <BankOutlined />, label: user.schoolName }; }
    return null;
  })();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => {
      setIsMobile(event.matches);
      setCollapsed(event.matches);
    };

    handleChange(mediaQuery);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      <Sider
        className="wl-sider"
        trigger={null}
        collapsible
        collapsed={collapsed}
        collapsedWidth={isMobile ? 0 : 80}
        zeroWidthTriggerStyle={{ display: 'none' }}
        style={{
          background:  'var(--wl-sider-bg)',
          height:      '100vh',
          overflowY:   'auto',
          overflowX:   'hidden',
          position:    'sticky',
          top:         0,
        }}
        width={240}
      >
        {/* Logo */}
        <div className="wl-logo-bar">
          {collapsed
            ? <span className="wl-logo-collapsed">🦷</span>
            : <img
                src="/logo/WECIRCLE_LOGO with Leaf.svg"
                alt="WeCircle"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextSibling.style.display = 'block';
                }}
              />
          }
          {!collapsed && (
            <span style={{ display: 'none', color: 'var(--wl-primary)', fontFamily: "'Fredoka One', sans-serif", fontSize: 18 }}>
              WeCircle
            </span>
          )}
        </div>

        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={filteredMenuItems}
          onClick={({ key }) => {
            navigate(key);
            if (isMobile) { setCollapsed(true); }
          }}
          style={{ borderRight: 0, marginTop: 8, background: 'transparent' }}
        />
      </Sider>

      <Layout style={{ height: '100vh', overflow: 'hidden' }}>
        <Header className="wl-header" style={{
          padding:    '0 24px',
          display:    'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          zIndex:     100,
          height:     69,
        }}>
        <Space
          className="wl-header-left"
          align="center"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          {collapsed ? (
            <MenuUnfoldOutlined
              style={{
                fontSize: 18,
                cursor: 'pointer',
                color: 'var(--wl-primary)',
              }}
              onClick={() => setCollapsed(false)}
            />
          ) : (
            <MenuFoldOutlined
              style={{
                fontSize: 18,
                cursor: 'pointer',
                color: 'var(--wl-primary)',
              }}
              onClick={() => setCollapsed(true)}
            />
          )}

          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div
              className="wl-greeting"
              style={{
                display: 'flex',
                alignItems: 'center',
                lineHeight: 1,
                fontFamily: "'Fredoka One', sans-serif",
                fontSize: 20,
                color: 'var(--wl-primary)',
                letterSpacing: 0.3,
                marginTop: 2,
              }}
            >
              Hello, {user?.name}!
            </div>
            {orgInfo && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: 6,
                    background: 'var(--wl-primary-pale)',
                    color: 'var(--wl-primary)',
                    fontSize: 12,
                  }}
                >
                  {orgInfo.icon}
                </span>
                <Text style={{ fontSize: 12, color: 'var(--wl-text-muted)', fontWeight: 500 }}>{orgInfo.label}</Text>
              </div>
            )}
          </div>
        </Space>

          <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }} placement="bottomRight">
            <Space className="wl-user-trigger" style={{ cursor: 'pointer' }}>
              <Avatar
                src={resolveFileUrl(user?.avatar) || undefined}
                style={{ background: 'var(--wl-primary-gradient)', fontFamily: "'Fredoka One', sans-serif" }}
              >
                {!user?.avatar && user?.name?.charAt(0)?.toUpperCase()}
              </Avatar>
              <div className="wl-user-meta" style={{ lineHeight: 1.3 }}>
                <Text strong style={{ fontSize: 13, display: 'block', color: 'var(--wl-text)' }}>{user?.name}</Text>
                <Text style={{ fontSize: 11, color: 'var(--wl-text-muted)' }}>{roleLabel[user?.role]}</Text>
              </div>
            </Space>
          </Dropdown>
        </Header>

        <Content className="site-layout-content" style={{ padding: '24px', background: 'var(--wl-bg)', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <Outlet />
        </Content>
      </Layout>

      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </Layout>
  );
};

export default AppLayout;
